import Info from "@assets/settings/info.svg";
import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer";
import { TransactionByIdResponseData } from "../../../services/models/Transactions/TransactionByIdModel";
import ConstantFunctions from "../../../utils/constants_functions";
import LabelRow from "./LabelRow";
import TransactionTypeSymbols from "./TransactionTypeSymbols";
import { getTransactionTypeText } from "./TransactionTypeText";

interface P2PTransactionContainerProps {
  transactionData: TransactionByIdResponseData;
}

const P2PTransactionContainer: React.FC<P2PTransactionContainerProps> = ({
  transactionData,
}) => {
  const constantFunctions = new ConstantFunctions();
  const userDetails = useSelector((state: AppState) => state.user);

  const displayValueOrDash = (value: any) => (value ? value.toString() : "--");
  const isValuePresent = (value: any) => !!value;

  var payinPayoutType = "";
  if (transactionData?.ledger === "CR") {
    payinPayoutType = "Credited";
  } else if (transactionData?.ledger === "DR") {
    payinPayoutType = "Debited";
  } else {
    payinPayoutType = "Unknown";
  }

  const isMaker = userDetails?.userId === transactionData?.makerId;
  const isBuyer = userDetails?.userId === transactionData?.buyerUserId;

  const role = isMaker
    ? `Maker ${isBuyer ? "(Buyer)" : "(Seller)"}`
    : `Taker ${isBuyer ? "(Buyer)" : "(Seller)"}`;

  const counterPartyEmail = isBuyer
    ? transactionData?.sellerEmailId
    : transactionData?.buyerEmailId;

  const mdr = isMaker
    ? transactionData?.makerFeePct
    : transactionData?.takerFeePct;

  const commission = isMaker
    ? transactionData?.makerFeeAmount
    : transactionData?.takerFeeAmount;

  return (
    <div className="border border-bg-header overflow-scroll mt-[20px] flex flex-col rounded-lg mx-[20px]">
      <div className="border-b border-b-bg-header px-[16px] py-[18px] flex flex-row justify-between">
        <div className="flex flex-row">
          <TransactionTypeSymbols
            transactionType={transactionData?.transactionType ?? ""}
            ledger={transactionData?.ledger ?? ""}
          />
          <span className="text-[16px] font-medium text-text-base">
            {transactionData?.ledger === "CR" ? "Received" : "Sent"}
          </span>
        </div>
        <div className="flex flex-row items-center gap-[4px]">
          <span className="text-[16px] font-bold text-text-base">
            {displayValueOrDash(
              constantFunctions.formatAmount(transactionData?.baseAmount),
            )}
          </span>
          <img
            src={transactionData?.cryptoCurrencyImage}
            width={18}
            height={18}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <LabelRow
          label={"Transaction Type"}
          value={displayValueOrDash(
            getTransactionTypeText(
              transactionData?.transactionType ?? "",
              transactionData?.internalType ?? "",
            ),
          )}
        />
        <LabelRow
          label={"Date & Time"}
          value={`${constantFunctions.formatDateTime(
            transactionData?.updatedAt ?? transactionData?.createdAt ?? "",
          )}`}
        />
      </div>
      <div className="border-b border-b-bg-header"></div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <div className="flex flex-row items-center gap-[4px]">
          <div className="bg-bg-header rounded-full h-[24px] p-[2px] w-[24px] mr-[8px] flex items-center justify-center">
            <img src={Info} alt="Info" />
          </div>
          <span className="text-[14px] font-normal text-text-base">
            P2P Transaction Details
          </span>
        </div>
        <LabelRow
          label={"Trade ID"}
          value={displayValueOrDash(transactionData?.tradeId)}
          isCopy={isValuePresent(transactionData?.tradeId)}
        />
        <LabelRow
          label={"Order ID"}
          value={displayValueOrDash(transactionData?.orderId)}
          isCopy={isValuePresent(transactionData?.orderId)}
        />
        <LabelRow label={"Role"} value={displayValueOrDash(role)} />
        <LabelRow
          label={"Counterparty Email"}
          value={displayValueOrDash(counterPartyEmail)}
          isCopy={isValuePresent(counterPartyEmail)}
        />
        <LabelRow
          label={"Payment Method"}
          value={displayValueOrDash(transactionData?.paymentGroupName)}
        />
        <LabelRow
          label={"Token | Fiat"}
          value={`${displayValueOrDash(
            transactionData.cryptoCurrencySymbol,
          )} | ${displayValueOrDash(transactionData.fiatCurrencySymbol)}`}
        />
        <LabelRow
          label={"Amount In fiat"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.paymentAmount ?? 0),
          )} ${displayValueOrDash(transactionData.fiatCurrencySymbol)}`}
        />
        <LabelRow
          label={"Amount In Crypto"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.tradeAmount ?? 0),
          )} ${displayValueOrDash(transactionData.cryptoCurrencySymbol)}`}
        />
        <LabelRow
          label={"Rate"}
          valueComp={
            <div className="flex flex-row items-center">
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                {displayValueOrDash(
                  constantFunctions.formatAmount(transactionData.pricePerUnit),
                )}
              </p>
              {transactionData.fiatCurrencyImage && (
                <img
                  src={transactionData.fiatCurrencyImage}
                  height={16}
                  width={16}
                  className="mr-[4px]"
                />
              )}
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                =
              </p>
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                1.00
              </p>
              {transactionData.cryptoCurrencyImage && (
                <img
                  src={transactionData.cryptoCurrencyImage}
                  height={16}
                  width={16}
                />
              )}
            </div>
          }
        />
        <LabelRow label={"MDR"} value={`${displayValueOrDash(mdr)}%`} />
        <LabelRow
          label={"Commission"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(commission),
          )} ${displayValueOrDash(transactionData.cryptoCurrencySymbol)}`}
        />
        <LabelRow
          label={`${payinPayoutType} In Crypto`}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData?.baseAmount),
          )} ${displayValueOrDash(transactionData.cryptoCurrencySymbol)}`}
        />
      </div>
    </div>
  );
};

export default P2PTransactionContainer;
